<template>
    <div class="clear-both">
        <input type="submit" class="button"
               :value="button"
               :disabled="disabled || isDisabled"
               :class="[isLoading ? 'yellow': (optionValues.buttonClass || 'green')]"
               @click.prevent="save"
        />

        <span class="form-status grey">
            <span v-if="isLoading">
                {{ optionValues.statesText.loading }}
                <base-icon16 :id="'loading'"/>
            </span>

            <span v-if="status === STATUS.SAVED">
                <base-icon16 :id="'yes'"/>
                {{ optionValues.statesText.saved }}
            </span>
            <span v-else-if="status === STATUS.RELOADING">
                <base-icon16 :id="'yes'"/>
                {{ optionValues.statesText.reloading }}
                <base-icon16 :id="'loading'"/>
            </span>
            <span v-else-if="status === STATUS.ERROR" class="red">
                <base-icon16 :id="'no'"/>
                {{ optionValues.statesText.error }}
            </span>
            <span v-else-if="status === STATUS.CUSTOM_ERROR" class="red">
                <base-icon16 :id="'no'"/>
                {{ optionValues.statesText.custom }}
            </span>
            <span v-else-if="(disabled || isDisabled) && optionValues.statesText.disabled">
                {{ optionValues.statesText.disabled }}
            </span>
        </span>
    </div>
</template>

<script>
import qs from "qs";

const STATUS = Object.freeze({
    SAVED: 2,
    RELOADING: 1,
    ERROR: 0,
    COMMON: -1,
    CUSTOM_ERROR: -2
});

export default {
    name: 'BaseSubmitBlock',
    components: {
        'base-icon16': () => import('@/components/base/BaseIcon16'),
    },
    props: {
        formStatus: Number,
        options: Object,
        button: {
            type: String,
            required: true
        },
        data: Object | Array,
        saveUrl: String,
        onSave: Function,
        onPrepareData: Function,
        onValidate: Function,
        disabled: Boolean
    },
    data() {
        return {
            STATUS,
            isLoading: false,
            status: STATUS.COMMON,
            optionValues: {
                buttonClass: '',
                successStatus: 2,
                reloadOnSuccess: false,
                statusTimeout: 3000,
                dataName: 'data',
                statesText: {
                    loading: this.$t('Loading') + '...',
                    saved: this.$t('Saved successfully'),
                    reloading: this.$t('Saved successfully. Page is reloading') + '...',
                    error: this.$t('Something wrong'),
                    custom: this.$t('Something wrong'),
                    disabled: ''
                },
                hideErrors: false,
                autoSubmit: false,
                redirectUrl: '',
            },
            isDisabled: false,
        }
    },
    created() {
        this.status = this.formStatus;
        for (let key in this.optionValues) {
            if (this.options?.[key] !== undefined) {
                if (key !== 'statesText') {
                    this.optionValues[key] = this.options[key];
                } else {
                    this.optionValues[key] = { ...this.optionValues[key], ...this.options[key] };
                }
            }
        }

        if (!this.onSave && !this.saveUrl && !this.data) {
            throw new TypeError(this.$t('Specify saveUrl and data props'));
        }
    },
    methods: {
        save() {
            const self = this;
            if (!self.isLoading && !self.disabled) {
                self.isLoading = true;
                self.status = STATUS.COMMON;
                self.$emit('pre-save');

                let postData = Object.assign({}, self.data);

                /* Prepare data before submit */
                if (self.onPrepareData) {
                    postData = self.onPrepareData(postData);
                }

                /* Validate data before submit */
                if (typeof self.onValidate === 'function') {
                    const validateResult = self.onValidate(postData);
                    if (typeof validateResult === 'string') {
                        self.optionValues.statesText.custom = validateResult;
                        this.status = STATUS.CUSTOM_ERROR;
                    } else if (!validateResult) {
                        this.status = STATUS.CUSTOM_ERROR;
                    }
                    if (this.status === STATUS.CUSTOM_ERROR) {
                        self.isLoading = false;
                        return false;
                    }
                }

                if (!self.onSave) {
                    self.$http.post(self.saveUrl,
                        qs.stringify({ _csrf: self.csrf, [self.optionValues.dataName]: postData }),
                        { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
                    ).then(function (response) {
                        self.status = response?.data?.status === 'fail' ? STATUS.ERROR : self.optionValues.successStatus;
                        if (self.status === STATUS.ERROR && response.data.errors.length && !self.optionValues.hideErrors) {
                            self.optionValues.statesText.error = response.data.errors;
                        }
                        self.$emit('saved', response);
                    }).catch(function (error) {
                        self.status = STATUS.ERROR;
                        self.$emit('failed', error);
                        console && console.warn(error);
                    }).finally(function () {
                        self.onFinally();
                    });
                } else {
                    self.$emit('saved');
                    self.onSave();
                    self.onFinally();
                }
            }
        },
        onFinally() {
            const self = this;
            self.$emit('finally');
            self.isLoading = false;
            if (!self.optionValues.reloadOnSuccess) {
                setTimeout(() => self.status = STATUS.COMMON, self.optionValues.statusTimeout);
            } else {
                self.isDisabled = true;
                if (this.optionValues.redirectUrl) {
                    location.href = this.optionValues.redirectUrl;
                } else {
                    location.reload();
                }
            }
        }
    },
    watch: {
        'options.autoSubmit': {
            deep: true,
            handler: function (newVal) {
                if (newVal) {
                    this.save();
                }
            }
        }

    }
}
</script>